import { useEffect, useRef, useState } from "react";
import MapsLoaderService from "services/map.loader.service";
import { borrowMap } from "utils/mapPool.util";
import { YELO_MAP_STYLE } from "utils/mapStyle.util";

/**
 * The journey rail's route preview — the design's `figure.journey-map`.
 *
 * select-class.html fills that slot with a flat screenshot of one Riyadh route
 * (assets/riyadh-route-map.png). This renders the real journey instead: the
 * actual pickup and destination, with the driving route between them.
 *
 * It is a preview, not a map to work in, so every gesture and control is off —
 * the design draws a still strip, and a pannable map inside a sticky rail also
 * swallows page scroll.
 *
 * Loading goes through MapsLoaderService so this shares the single
 * `maps/api/js` tag with the autocomplete and the pin picker; a second script
 * tag breaks the Places API for all of them.
 */

/**
 * A point arrives in one of two shapes, and both are live in this app:
 *   - `{ lat: 24.75, lng: 46.63 }` when the form was rehydrated from
 *     localStorage, where the LatLng was JSON-serialised to plain numbers
 *   - a `google.maps.LatLng`, where `lat` and `lng` are METHODS, when the value
 *     came straight from the Places API in this session
 * Reading `.lat` as a number silently fails on the second, which is the shape
 * you get on the normal path in from the home dock.
 */
type MapPoint =
  | { lat: number; lng: number }
  | { lat: () => number; lng: () => number };

type FitPadding = { top: number; right: number; bottom: number; left: number };

type JourneyMapProps = {
  variant?: "default" | "review";
  /** Padding for fitting the route, read at fit time so it can follow
   *  overlays (header, trip card) whose size depends on the screen. */
  getFitPadding?: () => FitPadding;
  pickup?: MapPoint | null;
  destination?: MapPoint | null;
  pickupKicker?: string;
  pickupLabel?: string;
  pickupMeta?: string;
  destinationKicker?: string;
  destinationLabel?: string;
  destinationMeta?: string;
};

type LatLng = { lat: number; lng: number };
type RouteChipVariant = "pickup" | "dropoff";
const ROUTE_CHIP_WORD_MAX = 2;
const ROUTE_CHIP_LABEL_MAX = 20;
const ROUTE_CHIP_EDGE_GUTTER = 8;

/** Normalises either shape to plain numbers, or null when it is neither. */
function toLatLng(point?: MapPoint | null): LatLng | null {
  if (!point) return null;
  const lat = typeof point.lat === "function" ? point.lat() : point.lat;
  const lng = typeof point.lng === "function" ? point.lng() : point.lng;
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
}

/* The design's own pin vocabulary, drawn as SVG so the map markers match the
   `.leg` pins beneath them: pickup is the ink dot, destination the yellow
   square. */
const PICKUP_PIN =
  "data:image/svg+xml;charset=UTF-8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"><circle cx="9" cy="9" r="5.5" fill="#0b090d" stroke="#fff" stroke-width="2.5"/></svg>`
  );

const DESTINATION_PIN =
  "data:image/svg+xml;charset=UTF-8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"><rect x="3.5" y="3.5" width="11" height="11" rx="3" fill="#ffd207" stroke="#fff" stroke-width="2.5"/></svg>`
  );

/* Review-screen palette. The confirm screen is a full-bleed map under the
   trip card, so it carries the brand: the route in Yelo purple on a white
   casing, pickup as the purple dot and destination as the yellow square —
   the same pin vocabulary as the journey rail — over a quiet, lightly
   lavender-tinted ground whose labels never compete with the route. */
const THEME_PURPLE = "#520a76";
const THEME_PURPLE_DEEP = "#3a004f";
const THEME_YELLOW = "#ffd207";

const REVIEW_PICKUP_PIN =
  "data:image/svg+xml;charset=UTF-8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="30"><circle cx="15" cy="15" r="14" fill="${THEME_PURPLE}" opacity=".16"/><circle cx="15" cy="15" r="8" fill="${THEME_PURPLE}" stroke="#fff" stroke-width="3"/></svg>`
  );

const REVIEW_DESTINATION_PIN =
  "data:image/svg+xml;charset=UTF-8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="30"><circle cx="15" cy="15" r="14" fill="${THEME_YELLOW}" opacity=".28"/><rect x="7.5" y="7.5" width="15" height="15" rx="4" fill="${THEME_YELLOW}" stroke="${THEME_PURPLE_DEEP}" stroke-width="2.5"/></svg>`
  );

const REVIEW_MAP_STYLE = [
  { featureType: "all", elementType: "geometry", stylers: [{ color: "#f6f4f7" }] },
  { featureType: "landscape", elementType: "geometry", stylers: [{ color: "#f6f4f7" }] },
  { featureType: "landscape.natural", elementType: "geometry", stylers: [{ color: "#f1eef3" }] },
  { featureType: "road", elementType: "geometry.stroke", stylers: [{ visibility: "off" }] },
  { featureType: "road.highway", elementType: "geometry.fill", stylers: [{ color: "#e3dce8" }] },
  { featureType: "road.arterial", elementType: "geometry.fill", stylers: [{ color: "#ebe6ee" }] },
  { featureType: "road.local", elementType: "geometry.fill", stylers: [{ color: "#ffffff" }] },
  { featureType: "road", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#e4dcea" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "administrative", elementType: "geometry", stylers: [{ visibility: "off" }] },
  { featureType: "all", elementType: "labels.text.fill", stylers: [{ color: "#9a90a0" }] },
  { featureType: "all", elementType: "labels.text.stroke", stylers: [{ color: "#ffffff" }, { weight: 3 }] },
  { featureType: "administrative.locality", elementType: "labels.text.fill", stylers: [{ color: "#7a6d82" }] },
  { featureType: "administrative.neighborhood", elementType: "labels.text.fill", stylers: [{ color: "#a79dad" }] },
];

function formatRouteChipLabel(label: string) {
  const compact = label.replace(/\s+/g, " ").trim();
  const words = compact.split(" ").filter(Boolean);
  const visibleWords = words.slice(0, ROUTE_CHIP_WORD_MAX).join(" ");
  const shortened =
    words.length > ROUTE_CHIP_WORD_MAX ? `${visibleWords}...` : visibleWords;

  if (shortened.length <= ROUTE_CHIP_LABEL_MAX) return shortened;
  return `${shortened.slice(0, ROUTE_CHIP_LABEL_MAX - 3).trimEnd()}...`;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function drawJourneyRoute({
  maps,
  map,
  path,
  approximate = false,
  review = false,
}: {
  maps: typeof google.maps;
  map: google.maps.Map;
  path: google.maps.LatLng[] | google.maps.LatLngLiteral[];
  approximate?: boolean;
  review?: boolean;
}) {
  if (approximate) {
    return [
      new maps.Polyline({
        map,
        path,
        clickable: false,
        strokeOpacity: 0,
        zIndex: 2,
        icons: [
          {
            icon: {
              path: "M 0,-1 0,1",
              strokeColor: review ? THEME_PURPLE : "#58007d",
              strokeOpacity: 0.72,
              strokeWeight: 3,
              scale: 2,
            },
            offset: "0",
            repeat: "14px",
          },
        ],
      }),
    ];
  }

  const casing = new maps.Polyline({
    map,
    path,
    clickable: false,
    strokeColor: "#ffffff",
    strokeOpacity: 0.92,
    strokeWeight: review ? 10 : 9,
    zIndex: 2,
  });
  const route = new maps.Polyline({
    map,
    path,
    clickable: false,
    strokeColor: review ? THEME_PURPLE : "#58007d",
    strokeOpacity: 1,
    strokeWeight: review ? 5.5 : 5,
    zIndex: 3,
    icons: review ? [] : [
      {
        icon: {
          path: maps.SymbolPath.FORWARD_CLOSED_ARROW,
          fillColor: "#ffd207",
          fillOpacity: 1,
          strokeColor: "#3a004f",
          strokeOpacity: 1,
          strokeWeight: 1,
          scale: 2.2,
        },
        offset: "18%",
        repeat: "76px",
      },
    ],
  });

  return [casing, route];
}

function createRouteChipOverlay({
  maps,
  map,
  position,
  variant,
  kicker,
  label,
  meta,
}: {
  maps: typeof google.maps;
  map: google.maps.Map;
  position: LatLng;
  variant: RouteChipVariant;
  kicker?: string;
  label?: string;
  meta?: string;
}): google.maps.OverlayView | null {
  if (!label) return null;
  const fullLabel = label.replace(/\s+/g, " ").trim();
  const visibleLabel = formatRouteChipLabel(fullLabel);

  class RouteChipOverlay extends maps.OverlayView {
    private node: HTMLDivElement | null = null;

    onAdd() {
      const node = document.createElement("div");
      node.className = `journey-route-chip journey-route-chip--${variant}`;
      node.title = fullLabel;

      const copy = document.createElement("span");
      const small = document.createElement("small");
      const strong = document.createElement("strong");
      small.textContent = kicker || "";
      strong.textContent = visibleLabel;
      copy.append(small, strong);
      node.append(copy);

      if (meta) {
        const badge = document.createElement("b");
        badge.textContent = meta;
        node.append(badge);
      }

      this.node = node;
      this.getPanes()?.floatPane.appendChild(node);
    }

    draw() {
      if (!this.node) return;
      const projection = this.getProjection();
      if (!projection) return;
      const latLng = new maps.LatLng(position.lat, position.lng);
      const divPoint = projection.fromLatLngToDivPixel(latLng);
      const containerPoint = projection.fromLatLngToContainerPixel(latLng);
      if (!divPoint || !containerPoint) return;

      const nodeWidth = this.node.offsetWidth;
      const nodeHeight = this.node.offsetHeight;
      const mapElement = map.getDiv();
      const maxLeft = mapElement.clientWidth - nodeWidth - ROUTE_CHIP_EDGE_GUTTER;
      const maxTop =
        mapElement.clientHeight - nodeHeight - ROUTE_CHIP_EDGE_GUTTER;
      const offsetX = variant === "dropoff" ? -nodeWidth + 10 : -10;
      const offsetY =
        variant === "dropoff" ? -nodeHeight / 2 : -nodeHeight - 12;
      const desiredContainerLeft = containerPoint.x + offsetX;
      const desiredContainerTop = containerPoint.y + offsetY;
      const nudgedContainerLeft = clamp(
        desiredContainerLeft,
        ROUTE_CHIP_EDGE_GUTTER,
        Math.max(ROUTE_CHIP_EDGE_GUTTER, maxLeft)
      );
      const nudgedContainerTop = clamp(
        desiredContainerTop,
        ROUTE_CHIP_EDGE_GUTTER,
        Math.max(ROUTE_CHIP_EDGE_GUTTER, maxTop)
      );

      this.node.style.left = `${
        divPoint.x + offsetX + nudgedContainerLeft - desiredContainerLeft
      }px`;
      this.node.style.top = `${
        divPoint.y + offsetY + nudgedContainerTop - desiredContainerTop
      }px`;
      this.node.style.transform = "none";
    }

    onRemove() {
      this.node?.remove();
      this.node = null;
    }
  }

  const overlay = new RouteChipOverlay();
  overlay.setMap(map);
  return overlay;
}

export default function JourneyMap({
  variant = "default",
  pickup,
  destination,
  pickupKicker,
  pickupLabel,
  pickupMeta,
  destinationKicker,
  destinationLabel,
  destinationMeta,
  getFitPadding,
}: JourneyMapProps) {
  const isReview = variant === "review";
  const holder = useRef<HTMLDivElement>(null);
  const fitPaddingRef = useRef(getFitPadding);
  fitPaddingRef.current = getFitPadding;
  const [ready, setReady] = useState(false);
  /* Drives the design's yellow/purple bar along the bottom edge, which is the
     progress indicator for the directions lookup — see `.journey-map-progress`
     in styles/yeloplus-select-class-extras.scss. */
  const [loading, setLoading] = useState(true);

  /* Waits on the shared loader, which never gives up: on slow data Maps can
     take longer than the 15 s this used to wait, after which the route stayed
     grey for good. The slot keeps the design's placeholder grey meanwhile. */
  useEffect(() => {
    if (window.google?.maps?.Map) {
      setReady(true);
      return;
    }

    let active = true;
    MapsLoaderService.whenReady().then(() => {
      if (active) setReady(true);
    });

    return () => {
      active = false;
    };
  }, []);

  /* Depend on the numbers, not the object: a `google.maps.LatLng` is a new
     instance on every Places result, so the raw props would retrigger the
     effect and rebuild the map for an unchanged journey. */
  const from = toLatLng(pickup);
  const to = toLatLng(destination);
  const fromKey = from ? `${from.lat},${from.lng}` : "";
  const toKey = to ? `${to.lat},${to.lng}` : "";

  useEffect(() => {
    if (!ready || !holder.current || !fromKey) return;
    const origin = toLatLng(pickup);
    const target = toLatLng(destination);
    if (!origin) return;

    const maps = window.google?.maps;
    if (!maps) return;

    /* From the shared pool: a map made per render was never freed by Google
       Maps (see utils/mapPool.util). */
    const borrowed = borrowMap(isReview ? "journey-review" : "journey", holder.current, {
      center: origin,
      zoom: 13,
      disableDefaultUI: true,
      gestureHandling: "none",
      keyboardShortcuts: false,
      clickableIcons: false,
      /* The pin picker's palette, so the strip and the map the reader drops a
         pin on are recognisably the same map. It keeps the POI and transit
         labels off, which is what the design's flat strip wants anyway. */
      styles: (isReview ? REVIEW_MAP_STYLE : YELO_MAP_STYLE) as google.maps.MapTypeStyle[],
    });
    const map = borrowed.map;

    const markers: google.maps.Marker[] = [
      new maps.Marker({
        map,
        position: origin,
        icon: isReview
          ? { url: REVIEW_PICKUP_PIN, anchor: new maps.Point(15, 15) }
          : { url: PICKUP_PIN, anchor: new maps.Point(9, 9) },
      }),
    ];
    const overlays: google.maps.OverlayView[] = [];
    const pickupOverlay = createRouteChipOverlay({
      maps,
      map,
      position: origin,
      variant: "pickup",
      kicker: pickupKicker,
      label: pickupLabel,
      meta: pickupMeta,
    });
    if (pickupOverlay) overlays.push(pickupOverlay);

    /* Hourly journeys have no destination — the rail shows a duration instead,
       so the map stays centred on the pickup, and there is no route to fetch
       for the progress bar to wait on. */
    if (!target) {
      map.setZoom(14);
      setLoading(false);
      return () => {
        overlays.forEach((overlay) => overlay?.setMap(null));
        markers.forEach((m) => m.setMap(null));
        borrowed.release();
      };
    }

    markers.push(
      new maps.Marker({
        map,
        position: target,
        icon: isReview
          ? { url: REVIEW_DESTINATION_PIN, anchor: new maps.Point(15, 15) }
          : { url: DESTINATION_PIN, anchor: new maps.Point(9, 9) },
      })
    );
    const destinationOverlay = createRouteChipOverlay({
      maps,
      map,
      position: target,
      variant: "dropoff",
      kicker: destinationKicker,
      label: destinationLabel,
      meta: destinationMeta,
    });
    if (destinationOverlay) overlays.push(destinationOverlay);

    let bounds = new maps.LatLngBounds();
    bounds.extend(origin);
    bounds.extend(target);
    const defaultPadding = isReview
      ? { top: 78, right: 48, bottom: 275, left: 48 }
      : { top: 70, right: 58, bottom: 76, left: 58 };
    const fit = () => {
      const el = holder.current;
      if (!el || !el.clientWidth || !el.clientHeight) return;
      const pad = fitPaddingRef.current?.() || defaultPadding;
      /* On a short screen the overlays can leave less room than the padding
         asks for; scale it down so the route still gets a usable window
         instead of being squeezed against one edge. */
      const spareY = el.clientHeight - pad.top - pad.bottom;
      const spareX = el.clientWidth - pad.left - pad.right;
      const minWindow = 90;
      const ky = spareY < minWindow ? Math.max(0, (el.clientHeight - minWindow) / (pad.top + pad.bottom)) : 1;
      const kx = spareX < minWindow ? Math.max(0, (el.clientWidth - minWindow) / (pad.left + pad.right)) : 1;
      map.fitBounds(bounds, {
        top: Math.round(pad.top * ky),
        bottom: Math.round(pad.bottom * ky),
        left: Math.round(pad.left * kx),
        right: Math.round(pad.right * kx),
      });
    };
    fit();

    /* The map's box changes with the viewport (rotation, device switch,
       keyboard, the action bar growing), and fitBounds is a one-off — without
       re-fitting, the route stays framed for the old size. */
    let resizeFrame = 0;
    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => {
            cancelAnimationFrame(resizeFrame);
            resizeFrame = requestAnimationFrame(fit);
          })
        : null;
    if (holder.current) observer?.observe(holder.current);

    /* The real driving route. If Directions is unavailable (quota, no
       route between the two points), the markers and bounds still stand — the
       strip degrades to origin/destination rather than breaking. */
    let routeLines: google.maps.Polyline[] = [];

    let cancelled = false;
    new maps.DirectionsService().route(
      {
        origin,
        destination: target,
        travelMode: maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (cancelled) return;
        /* The bar stops on failure too: the markers and bounds are already
           drawn, so the strip is finished either way. */
        setLoading(false);
        if (status !== "OK" || !result) {
          routeLines = drawJourneyRoute({
            maps,
            map,
            path: [origin, target],
            approximate: true,
            review: isReview,
          });
          return;
        }
        const routePath = result.routes?.[0]?.overview_path;
        if (routePath?.length) {
          routeLines = drawJourneyRoute({ maps, map, path: routePath, review: isReview });
        }
        const routeBounds = result.routes?.[0]?.bounds;
        if (routeBounds) {
          bounds = routeBounds;
          fit();
        }
      }
    );

    return () => {
      cancelled = true;
      observer?.disconnect();
      cancelAnimationFrame(resizeFrame);
      routeLines.forEach((line) => line.setMap(null));
      overlays.forEach((overlay) => overlay?.setMap(null));
      markers.forEach((m) => m.setMap(null));
      borrowed.release();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ready,
    fromKey,
    toKey,
    pickupKicker,
    pickupLabel,
    pickupMeta,
    destinationKicker,
    destinationLabel,
    destinationMeta,
    isReview,
  ]);

  /* No coordinates yet (the form is still hydrating): render the slot empty
     rather than an error state, so the panel keeps its height and the rail
     does not jump when the map arrives. */
  return (
    <>
      <div className="journey-map-canvas" ref={holder} aria-hidden="true" />
      {/* The bar stays after the route lands.
       *
       * It used to unmount on load, which took a yellow rule off the bottom
       * edge of the map a second or two after the reader arrived — a thing that
       * appears, then leaves, drawing the eye twice for nothing. It fills to
       * 100% instead and stays as the map's bottom edge.
       *
       * It only claims to be a progressbar while it is one; once full it is
       * decoration and says so, rather than announcing a permanent 92%. */}
      <span
        className={
          loading ? "journey-map-progress is-loading" : "journey-map-progress"
        }
        role={loading ? "progressbar" : undefined}
        aria-label={loading ? "Loading route" : undefined}
        aria-hidden={loading ? undefined : true}
      />
    </>
  );
}
